import { ChangeEvent, Dispatch, SetStateAction, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { camelCase } from 'change-case';

import * as constants from '@/constants';
import i18n from '@/settings/i18n';
import { api } from '@/services/api/axios/instance';
import { translateApiError } from '@/utils/translateApiError';
import {
  ErrorCSVFormat,
  ErrorUsersValidation,
  User,
  ValidationError,
} from '@/types/userList';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

export type UpdateError = {
  rowNo: number;
  message: string;
};

type Props = {
  currentOrganizationId: number;
  setCsvFormatError: Dispatch<SetStateAction<string | null>>;
  setCsvUpdateErrors: Dispatch<SetStateAction<UpdateError[] | null>>;
  setIsPreview: Dispatch<SetStateAction<boolean>>;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
  setSelectedUserId: Dispatch<SetStateAction<number | null>>;
  setUsers: Dispatch<SetStateAction<User[] | null>>;
};

export const useUploadCsv = ({
  currentOrganizationId,
  setCsvFormatError,
  setCsvUpdateErrors,
  setIsPreview,
  setIsUploading,
  setSelectedUserId,
  setUsers,
}: Props) => {
  const { apiErrorHandler } = useApiErrorHandler();
  const onUploadCsvClick = useCallback<
    (e: ChangeEvent<HTMLInputElement>) => void
  >(
    async (e) => {
      e.persist();
      e.preventDefault();
      e.stopPropagation();

      if (!e.target.files || e.target.value === '') {
        return;
      }

      const formData = new FormData();
      formData.append('csv', e.target.files[0]);

      await uploadCsv({
        formData,
        organizationId: currentOrganizationId,
        setCsvFormatError,
        setCsvUpdateErrors,
        apiErrorHandler,
        setIsPreview,
        setIsUploading,
        setSelectedUserId,
        setUsers,
      });

      // 同じファイルを選択しても onChange を発火させて再度CSVアップロードできるよう value を初期化
      e.target.value = '';
    },
    [
      apiErrorHandler,
      currentOrganizationId,
      setCsvFormatError,
      setCsvUpdateErrors,
      setIsPreview,
      setIsUploading,
      setSelectedUserId,
      setUsers,
    ],
  );

  return { onUploadCsvClick };
};

type UploadCsvProps = {
  formData: FormData;
  organizationId: number;
  setCsvFormatError: Dispatch<SetStateAction<string | null>>;
  setCsvUpdateErrors: Dispatch<SetStateAction<UpdateError[] | null>>;
  apiErrorHandler: (err: any) => void;
  setIsPreview: Dispatch<SetStateAction<boolean>>;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
  setSelectedUserId: Dispatch<SetStateAction<number | null>>;
  setUsers: Dispatch<SetStateAction<User[] | null>>;
};

export const uploadCsv = async ({
  formData,
  organizationId,
  setCsvFormatError,
  setCsvUpdateErrors,
  apiErrorHandler,
  setIsPreview,
  setIsUploading,
  setSelectedUserId,
  setUsers,
}: UploadCsvProps) => {
  const path = `/organizations/${organizationId}/users/preview`;
  const options = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };

  try {
    setSelectedUserId(null);
    setCsvFormatError(null);
    setCsvUpdateErrors(null);
    setIsPreview(false);
    setIsUploading(true);

    const { data }: AxiosResponse<{ users: User[] }> = await api.post(
      path,
      formData,
      options,
    );

    setUsers(data.users);
    setIsPreview(true);
  } catch (e) {
    handleUploadError({
      e,
      setCsvFormatError,
      setCsvUpdateErrors,
      apiErrorHandler,
      setIsPreview,
      setUsers,
    });
  } finally {
    setIsUploading(false);
  }
};

type HandleUploadErrorProps = {
  e: any;
  setCsvFormatError: Dispatch<SetStateAction<string | null>>;
  setCsvUpdateErrors: Dispatch<SetStateAction<UpdateError[] | null>>;
  apiErrorHandler: (err: any) => void;
  setIsPreview: Dispatch<SetStateAction<boolean>>;
  setUsers: Dispatch<SetStateAction<User[] | null>>;
};

const handleUploadError = ({
  e,
  setCsvFormatError,
  setCsvUpdateErrors,
  apiErrorHandler,
  setIsPreview,
  setUsers,
}: HandleUploadErrorProps) => {
  switch (e.response && e.response.status) {
    case 400: {
      // CSVのファイルフォーマットエラー
      const data = e.response.data as ErrorCSVFormat;
      const message = i18n.t(
        `ui.user.csv.formatError.${camelCase(data.error)}`,
      );
      setCsvFormatError(message);
      setIsPreview(true);
      break;
    }
    case 422: {
      // CSVデータのバリデーションエラー
      const data = e.response.data as ErrorUsersValidation;
      const errors = formatUserErrors(data.errors);
      setUsers(data.users);
      setCsvUpdateErrors(errors);
      setIsPreview(true);
      break;
    }
    case 413:
      // ファイルサイズが大きい
      setCsvFormatError(i18n.t(`ui.user.csv.formatError.invalidCsvFormat`));
      setIsPreview(true);
      break;
    default:
      apiErrorHandler(e);
  }
};

// CSVアップロードのバリデーションエラーをメッセージに変換
const formatUserErrors = (errors: ValidationError[]): UpdateError[] => {
  if (!errors) {
    return [];
  }

  const filteredErrors = filterErrors(errors);

  const formattedErrors = filteredErrors
    .map((error) => formatError(error))
    .filter((o) => o) as UpdateError[];

  return formattedErrors.sort((a, b) => a.rowNo - b.rowNo);
};

// APIから以下のような同じフィールドに対して複数のエラーメッセージが返されるため1つにまとめる
// 例）
//   5行目 メールアドレス: データが存在しません
//   5行目 メールアドレス: 入力してください
//   5行目 メールアドレス: 不正なデータです
const filterErrors = (errors: ValidationError[]) => {
  let errorFields: Array<{ rowNo: string; field: string }> = [];

  return errors.filter((error) => {
    const rowNo = error.meta && error.meta.rowNo;
    const field = error.field;

    if (!rowNo || !field) {
      return false;
    }

    const isExisted = isExistedError(rowNo, field, errorFields);
    errorFields = [...errorFields, { rowNo, field }];

    return !isExisted;
  });
};

const isExistedError = (
  rowNo: string,
  field: string,
  errorFields: Array<{ rowNo: string; field: string }>,
) => {
  return !!errorFields.find((o) => {
    if (o.rowNo === rowNo && o.field === field) {
      return true;
    }
    return false;
  });
};

const formatError = (error: ValidationError) => {
  const rowNo = error.meta && error.meta.rowNo;
  const field = error.field;
  let code = error.code;

  if (!rowNo || !field || !code) {
    return null;
  }

  if (field === 'name_id' && code == 'invalid_format') {
    code = constants.NAME_ID_FORMAT_ERROR;
  }

  return {
    rowNo,
    message: errorMessage(rowNo, field, code),
  };
};

const errorMessage = (rowNo: number, field: string, code: string) =>
  i18n.t('ui.user.csv.validationError', {
    rowNo,
    fieldName: i18n.t(`models.user.${camelCase(field)}`),
    message: translateApiError('user', field, code),
  });
